/** Image / Asset compact node — media-first "asset" card
 * (VISTOERA_CONTENT_CANVAS_MEDIA_CARD_AND_GENERATION_INTERACTION_V2
 * §1–§8, superseding VISTOERA_PRODUCTION_CANVAS_NODE_DETAIL_SHELL_AND_IMAGE_ASSET_UI_V2
 * §5–§9 / VISTOERA_PRODUCTION_CANVAS_IMAGE_TO_IMAGE_GENERATION_FLOW_V1 §4–§7).
 *
 * "图片 / 素材" — an image that entered THIS project via upload/pick/
 * asset-library, regardless of whether the underlying `SiteAsset` was
 * originally AI-generated elsewhere (spec §1 — card type is decided by
 * "如何进入当前项目", never by asset provenance).
 *
 * Full image, real aspect ratio, never cropped (spec §2 —
 * `computeMediaBoxSize` sizes the WHOLE card to the image's shape, the
 * `<img>` itself uses `object-fit: contain` only as the letterbox
 * fallback once clamped). Header carries no more status dot (spec §3);
 * hover reveals `[复制][更换图片][删除]` (spec §3/§4) plus a right-side
 * workflow toolbar for "生成图片"/"生成视频" (spec §6) and a large
 * hover-only circular connection handle at the media body's vertical
 * center (spec §7). Single click (bound node) toggles the existing
 * Node Detail Shell panel unchanged; double click opens the full-size
 * lightbox — the two are disambiguated by `useSingleOrDoubleClick` so
 * they never both fire on one real double-click (spec §5/§46). */

import { CloseOutlined, CopyOutlined, DownloadOutlined, EditOutlined, LoadingOutlined, PictureOutlined, ReloadOutlined, SwapOutlined } from '@ant-design/icons'
import { Handle, Position, useUpdateNodeInternals, type NodeProps } from '@xyflow/react'
import { Button, Tooltip } from 'antd'
import {
  lazy,
  memo,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type DragEvent as ReactDragEvent,
  type MouseEvent as ReactMouseEvent,
} from 'react'
import { isOsFileDrag } from '../canvasFileDrop'
import { useTranslation } from 'react-i18next'
import { useCanvasNodeRegistryI18n } from '../nodeRegistryI18n'
import { loadImagePickerItemDetailDeduped } from '../canvasSharedConfigLoaders'
import { useNodeDefinition } from '../nodeRegistry'
import { computeMediaBoxSize } from '../mediaBoxSize'
import { functionCardShowsInspector } from '../functionCardInteractionV2'
import { canvasNodeShellStateClass } from '../canvasNodeShellV2'
import MediaLightbox, { type CanvasHiResMasterMeta } from '../MediaLightbox'
import { useSingleOrDoubleClick } from '../useSingleOrDoubleClick'
import { downloadCanvasMedia } from '../canvasDownload'
import { siteAssetDisplayPreviewUrl, siteAssetMasterUrl, siteAssetThumbnailUrl } from '../canvasImageQuality'
import { useCanvasMediaViewportGate, useCanvasProgressiveImageSrc } from '../useCanvasProgressiveImage'
import MediaWorkflowRail from './MediaWorkflowRail'
// Editor Stability P0 (VistoeraTASK-00103) — local error boundary, NOT
// lazy: it must be available to catch a crash from the very first render
// of the (lazy) editor chunk below, and it is a tiny class component with
// no heavy runtime of its own (no canvas/pointer/brush code), so there is
// no meaningful bundle-size reason to split it out. See its own doc
// comment for the full "why this exists" story.
import ImageEditorErrorBoundary from './ImageEditorErrorBoundary'
import { createEditorDiagnosticsSnapshot, type EditorDiagnosticsRef } from '../../../../../components/editor/editorDiagnostics'
import type { FlowNodeData } from '../types'

// P5/P36 — the shared Editor CORE (`DualPaneCutoutEditor`) is a heavy
// canvas/pointer/brush runtime; every Image / Asset node on a busy Canvas
// must NOT pay for it until the user actually clicks "编辑图片" on THAT
// node. A dynamic import here is a real Vite/Rollup code-split boundary —
// this chunk is only ever requested (and only ever mounted, via the
// `editorOpen` gate below) the first time some node's editor opens.
const CanvasImageEditorOverlay = lazy(() => import('./CanvasImageEditorOverlay'))

function mapPickerOrigin(sourceKind: string | null | undefined): CanvasHiResMasterMeta['origin'] {
  if (sourceKind === 'generated') return 'generated'
  if (sourceKind === 'uploaded' || sourceKind === 'asset_library') return 'uploaded'
  return 'unknown'
}

function ImageAssetNodeImpl({ id, data, selected }: NodeProps) {
  const nodeData = data as unknown as FlowNodeData & { workspaceId?: string }
  const def = useNodeDefinition(nodeData.nodeType)
  const { t } = useTranslation('canvas')
  const { nodeInstanceLabel } = useCanvasNodeRegistryI18n()
  const label = nodeInstanceLabel(nodeData.nodeType, nodeData.fields?.label as string | undefined, def.label)
  const entityId = nodeData.entityId
  const workspaceId = nodeData.workspaceId
  const siteId = nodeData.siteId
  // VistoeraTASK-00134 — client-only upload lifecycle for a node created
  // from a local OS file drop, before its canonical `SiteAsset` exists
  // (see `types.ts`'s `localUpload` doc comment for the full contract).
  const localUpload = nodeData.localUpload || null
  const hasPreviewSurface = Boolean(entityId || localUpload)

  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null)
  const [displayPreviewUrl, setDisplayPreviewUrl] = useState<string | null>(null)
  // VistoeraTASK-00147 — an error on the `web` preview tier forces a
  // fallback to `thumbnail` regardless of what zoom/viewport would
  // otherwise pick; cleared on manual retry. `null` = no override, defer
  // entirely to `useCanvasProgressiveImageSrc`'s own zoom-based choice.
  const [qualityRoleOverride, setQualityRoleOverride] = useState<'thumbnail_fallback' | null>(null)
  const [imageRetryNonce, setImageRetryNonce] = useState(0)
  const [masterMeta, setMasterMeta] = useState<CanvasHiResMasterMeta | null>(null)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [box, setBox] = useState(() => computeMediaBoxSize(0, 0))
  const updateNodeInternals = useUpdateNodeInternals()

  // VistoeraTASK-00067 §2/§3/§35 — single-expanded-card contract, same as
  // `video_media`/`generator` cards (see `CanvasBoard.onNodeClick`).
  // `editorOpen` is intentionally its OWN, separate boolean (not derived
  // from `isDetailExpanded`) so closing the inspector strip never
  // force-closes an in-progress edit, and — critically for §35/§6 — the
  // heavy `CanvasImageEditorOverlay` chunk/component only ever mounts
  // while this is `true`, never merely while the card is expanded.
  const isDetailExpanded = Boolean(nodeData.isDetailExpanded)
  const showInspector = functionCardShowsInspector(isDetailExpanded, nodeData.isDragTempCollapsed)
  const [editorOpen, setEditorOpen] = useState(false)
  // Editor Stability P0 (VistoeraTASK-00103) — bumped only by
  // `ImageEditorErrorBoundary`'s own "重试" button (via `handleEditorRetry`
  // below). Used as this whole subtree's React `key`: incrementing it
  // fully unmounts + remounts BOTH the boundary (resetting its own
  // `hasError` state) AND a genuinely fresh `CanvasImageEditorOverlay`/
  // `DualPaneCutoutEditor` instance underneath — new refs, new state, new
  // effects, zero chance of anything from the crashed instance (a stale
  // async completion, a leftover object URL, …) touching the new one.
  // Never bumped on a normal open/close — those already get a fresh
  // instance for free via the `editorOpen &&` mount gate below.
  const [editorInstanceKey, setEditorInstanceKey] = useState(0)
  // VistoeraTASK-00111 P4/P7/P13-P17 — a genuinely FRESH diagnostics box
  // every time this subtree actually (re)mounts: either a normal
  // close→reopen (`editorOpen` flips `false`→`true`) or a boundary
  // "重试" (`editorInstanceKey` bumps). Handed to BOTH the boundary
  // (reads it in `componentDidCatch`) and the overlay (patches it as the
  // editor progresses) below — same object identity, no lookup/matching
  // needed since exactly one editor subtree is ever mounted per node.
  const diagnosticsRef: EditorDiagnosticsRef = useMemo(
    () => ({ current: createEditorDiagnosticsSnapshot() }),
    [editorOpen, editorInstanceKey],
  )

  // Defensive re-measure (P1 spec ?26 root-cause) ? whenever this
  // node's real rendered box changes size (the image finishes loading
  // and `computeMediaBoxSize` replaces the placeholder box), explicitly
  // tell React Flow to re-read both Handles' live DOM position rather
  // than trust whatever it cached at the previous size. Cheap
  // insurance on top of the structural `overflow` fix above ? it costs
  // nothing when the size hasn't actually changed between renders.
  useEffect(() => {
    const frame = window.requestAnimationFrame(() => updateNodeInternals(id))
    return () => window.cancelAnimationFrame(frame)
  }, [id, box.width, box.height, updateNodeInternals])

  const withDisplayRetry = useCallback((url: string) => {
    if (!imageRetryNonce) return url
    const sep = url.includes('?') ? '&' : '?'
    return `${url}${sep}displayRetry=${imageRetryNonce}`
  }, [imageRetryNonce])

  // VistoeraTASK-00147 — canonical URLs are pure string construction (no
  // network), so they're always resolved immediately regardless of
  // viewport. Only the ACTUAL request (the media gate below) and the
  // richer-detail metadata fetch are gated.
  useEffect(() => {
    setMasterMeta(null)
    setQualityRoleOverride(null)
    if (!entityId || !workspaceId) {
      setThumbnailUrl(null)
      setDisplayPreviewUrl(null)
      return
    }
    setDisplayPreviewUrl(siteAssetDisplayPreviewUrl(workspaceId, entityId))
    setThumbnailUrl(siteAssetThumbnailUrl(workspaceId, entityId))
  }, [entityId, workspaceId])

  // VistoeraTASK-00147 §四/§十四 — viewport-aware media gate: on a large
  // Canvas, only nodes actually near the visible viewport ever issue a
  // real image-variant request at all (root-caused via real Live Preview
  // forensics against a 125-node canvas — see
  // `useCanvasProgressiveImage.ts`'s own doc comment for the numbers).
  const mediaGate = useCanvasMediaViewportGate()

  // Richer asset detail (preview_url/thumbnail_url/master meta) is a
  // small JSON call, not image bytes, but still gated the same way so an
  // off-screen node contributes ZERO network activity of any kind until
  // it is actually about to be shown.
  useEffect(() => {
    if (!entityId || !workspaceId || !mediaGate.isNearViewport) return
    let cancelled = false
    loadImagePickerItemDetailDeduped(workspaceId, `sa:${entityId}`, { include_usage: false, include_variants: false })
      .then((detail) => {
        if (cancelled) return
        if (detail.preview_url) setDisplayPreviewUrl(detail.preview_url)
        if (detail.thumbnail_url) setThumbnailUrl(detail.thumbnail_url)
        // Master metadata comes from SiteAsset original fields — never web/thumbnail.
        setMasterMeta({
          width: detail.width,
          height: detail.height,
          sizeBytes: detail.size_bytes,
          createdAt: detail.created_at || detail.ai_provenance?.generated_at || null,
          origin: mapPickerOrigin(detail.source_kind),
        })
      })
      .catch(() => {
        /* Compact preview degrades to the placeholder icon — the detail
         * panel below reports the real error if the user expands it. */
      })
    return () => {
      cancelled = true
    }
  }, [entityId, workspaceId, mediaGate.isNearViewport])

  const progressiveImage = useCanvasProgressiveImageSrc({
    thumbnailUrl: thumbnailUrl ? withDisplayRetry(thumbnailUrl) : null,
    previewUrl: qualityRoleOverride === 'thumbnail_fallback' ? null : displayPreviewUrl ? withDisplayRetry(displayPreviewUrl) : null,
    isNearViewport: mediaGate.isNearViewport,
    renderedWidthPx: mediaGate.renderedWidthPx,
    resetKey: entityId || null,
    retryNonce: imageRetryNonce,
  })
  const imageDisplaySrc = progressiveImage.src
  const imageLoadFailed = progressiveImage.hasError
  const imageMediaLoading = progressiveImage.isLoading
  const imageQualityRole = progressiveImage.tier === 'thumbnail' ? 'thumbnail_fallback' : 'display_preview'

  const handleCanonicalImageError = () => {
    if (!entityId || !workspaceId) {
      progressiveImage.markError()
      return
    }
    if (progressiveImage.tier === 'preview' && thumbnailUrl) {
      // The higher tier failed — force the fallback tier rather than the
      // zoom-driven choice, matching this card's pre-existing "preview
      // fails -> try thumbnail -> then explicit failure" ladder.
      setQualityRoleOverride('thumbnail_fallback')
      return
    }
    progressiveImage.markError()
  }

  const handleImageLoadRetryClick = (e: ReactMouseEvent) => {
    e.stopPropagation()
    if (!entityId || !workspaceId) return
    setQualityRoleOverride(null)
    setImageRetryNonce((n) => n + 1)
  }

  const openPicker = () => {
    if (!siteId || !nodeData.onOpenAssetPicker) return
    // Bound node → single replace. Empty node → multi-select; first
    // selection binds here, remaining become sibling image_asset nodes.
    if (entityId) {
      nodeData.onOpenAssetPicker({
        nodeId: id,
        purpose: 'graphic_replace',
        title: t('mediaCard.replaceImage'),
        selectionMode: 'single',
        maxSelection: 1,
        onConfirm: (selections) => {
          const first = selections[0]
          if (!first) return
          nodeData.onChangeEntity?.(id, { entityType: 'site_asset', entityId: first.assetId })
        },
      })
      return
    }
    nodeData.onOpenAssetPicker({
      nodeId: id,
      purpose: 'canvas_insert',
      title: t('mediaCard.selectImage'),
      selectionMode: 'multiple',
      maxSelection: 20,
      onConfirm: (selections) => {
        if (!selections.length) return
        if (nodeData.onMaterializeImageAssets) {
          nodeData.onMaterializeImageAssets(selections, { bindNodeId: id })
          return
        }
        const first = selections[0]
        if (first) nodeData.onChangeEntity?.(id, { entityType: 'site_asset', entityId: first.assetId })
      },
    })
  }

  const handleEmptyClick = () => {
    openPicker()
  }

  const handleOsFileDragOver = (e: ReactDragEvent) => {
    if (!isOsFileDrag(e.dataTransfer) || entityId || localUpload) return
    e.preventDefault()
    e.stopPropagation()
    e.dataTransfer.dropEffect = 'copy'
  }

  const handleOsFileDrop = (e: ReactDragEvent) => {
    if (!isOsFileDrag(e.dataTransfer) || entityId || localUpload) return
    e.preventDefault()
    e.stopPropagation()
    const files = e.dataTransfer.files
    if (!files?.length || !nodeData.onOsImageFilesDroppedOnNode) return
    nodeData.onOsImageFilesDroppedOnNode(id, Array.from(files))
  }

  const { onClick: onMediaSingleClick, onDoubleClick: onMediaDoubleClick } = useSingleOrDoubleClick(
    () => {
      /* Selection / expand ownership lives on CanvasBoard.onNodeClick —
       * media body single-click must not steal expandedNodeId. */
    },
    () => setPreviewOpen(true),
  )

  const handleSwapClick = (e: ReactMouseEvent) => {
    e.stopPropagation()
    openPicker()
  }

  const handleCopyClick = (e: ReactMouseEvent) => {
    e.stopPropagation()
    nodeData.onDuplicateNode?.(id)
  }

  const handleDownloadClick = (e: ReactMouseEvent) => {
    e.stopPropagation()
    if (!entityId || !workspaceId) return
    // Full-quality `original` variant (spec §20/§30 — Download Uniformity:
    // every media card's header hover actions needs `[下载]`, not just the
    // two generated-result card types), same `?download=true` fix as
    // `GenerateImageNode.tsx` (see `canvasDownload.ts`'s doc comment).
    downloadCanvasMedia(siteAssetMasterUrl(workspaceId, entityId))
  }

  const handleDeleteClick = (e: ReactMouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    // A still-local (never persisted) upload placeholder has no
    // Canvas-node-delete-confirm / asset-lifecycle concerns at all —
    // just drop it client-side (spec §4 "Remove", §12 node deletion ≠
    // asset deletion distinction doesn't even apply here yet).
    if (localUpload) {
      nodeData.onRemoveLocalImageUpload?.(id)
      return
    }
    nodeData.onRequestDelete?.(id, e.currentTarget)
  }

  const handleRetryLocalUploadClick = (e: ReactMouseEvent) => {
    e.stopPropagation()
    nodeData.onRetryLocalImageUpload?.(id)
  }

  const handleRemoveLocalUploadClick = (e: ReactMouseEvent) => {
    e.stopPropagation()
    nodeData.onRemoveLocalImageUpload?.(id)
  }

  const handleQuickGenerate = (kind: 'image' | 'video' | 'text') => {
    nodeData.onQuickGenerate?.(id, kind)
  }

  // VistoeraTASK-00067 §4 — "编辑图片" lives in the expanded inspector
  // strip only (never the header), opens the Canvas-mode Vistoera 图像
  // 编辑器 as an in-Canvas overlay (never a route navigation — Canvas
  // project/node/asset context stays exactly as-is underneath it).
  const handleEditImageClick = (e: ReactMouseEvent) => {
    e.stopPropagation()
    if (!entityId || !workspaceId || !siteId) return
    setEditorOpen(true)
  }

  // §26/§27/§28/§29 — the overlay itself already created the brand-new
  // `SiteAsset` before calling this; this node's only remaining job is to
  // ask `CanvasBoard` (which owns node/edge state) to place the new
  // sibling node beside THIS one. The source node/asset are never mutated.
  // VistoeraTASK-00300 — returns the Board's own `Promise<boolean>` (true
  // only once genuinely persisted) so the overlay can await the TRUE
  // completion condition rather than firing an un-awaitable `void`
  // callback immediately followed by closing itself.
  const handleEditorSavedAsNewAsset = async (newAssetId: string): Promise<boolean> => {
    const ok = await nodeData.onSaveEditedImageAsNewAsset?.(id, newAssetId)
    return ok ?? false
  }

  // Editor Stability P0 (VistoeraTASK-00103) — see `editorInstanceKey`'s
  // doc comment above and `ImageEditorErrorBoundary`'s own doc comment.
  const handleEditorRetry = () => {
    setEditorInstanceKey((k) => k + 1)
  }

  return (
    <div
      ref={mediaGate.setRef}
      className={`pcv-node pcv-node--media${selected ? ' is-selected' : ''}${!hasPreviewSurface ? ' is-empty' : ''}${localUpload?.status === 'uploading' || localUpload?.status === 'reconciling' ? ' is-local-uploading' : ''}${localUpload?.status === 'error' ? ' is-local-upload-error' : ''} ${canvasNodeShellStateClass(showInspector)}`}
      style={{ width: hasPreviewSurface ? box.width : undefined }}
      onClick={hasPreviewSurface ? undefined : handleEmptyClick}
    >
      {/* Left/right connection Handles are DIRECT CHILDREN of this outer
       * node div (P1 spec §21–§27) — siblings of `.pcv-node-inner`
       * below, never nested inside it, so its `overflow: hidden` (needed
       * to clip the image itself) can never also clip half of a Handle
       * that's meant to straddle the card's own edge. Both live at this
       * whole node's vertical center (header + body, spec §22) via
       * React Flow's own default `top: 50%` positioning — no explicit
       * pixel `top` override needed here since this card has no
       * collapsible Inspector below to stay clear of. */}
      <Handle type="target" position={Position.Left} className="pcv-handle pcv-handle--lg" />
      <div className="pcv-node-inner">
        <div className="pcv-media-header">
          <span className="pcv-media-icon" style={{ color: def.color, background: `${def.color}22` }}>
            {def.icon}
          </span>
          <span className="pcv-media-title" title={label}>
            {label}
          </span>
          {/* Node self-actions (spec §3/§5) — 下载/复制/更换图片/删除,
           * flush right in the header row itself now, never floating
           * over the image content. */}
          <div className="pcv-media-hover-toolbar nodrag">
            {localUpload ? (
              <Tooltip
                title={localUpload.status === 'error' ? t('common.retry') : localUpload.filename}
                mouseEnterDelay={0.3}
              >
                <span className="pcv-media-local-upload-status" data-testid="image-asset-local-upload-status">
                  {localUpload.status === 'uploading' || localUpload.status === 'reconciling' ? (
                    <LoadingOutlined spin />
                  ) : null}
                  {localUpload.status === 'error' ? (
                    <button
                      type="button"
                      className="pcv-media-hover-btn"
                      aria-label={t('common.retry')}
                      onClick={handleRetryLocalUploadClick}
                    >
                      <ReloadOutlined />
                    </button>
                  ) : null}
                </span>
              </Tooltip>
            ) : null}
            {entityId ? (
              <Tooltip title={t('mediaLightbox.downloadOriginal', { defaultValue: 'Download original' })} mouseEnterDelay={0.3}>
                <button type="button" className="pcv-media-hover-btn" aria-label={t('mediaLightbox.downloadOriginal')} onClick={handleDownloadClick}>
                  <DownloadOutlined />
                </button>
              </Tooltip>
            ) : null}
            {entityId ? (
              <Tooltip title={t('common.copy')} mouseEnterDelay={0.3}>
                <button type="button" className="pcv-media-hover-btn" aria-label={t('common.copy')} onClick={handleCopyClick}>
                  <CopyOutlined />
                </button>
              </Tooltip>
            ) : null}
            {entityId ? (
              <Tooltip title={t('mediaCard.replaceImage')} mouseEnterDelay={0.3}>
                <button type="button" className="pcv-media-hover-btn" aria-label={t('mediaCard.replaceImage')} onClick={handleSwapClick}>
                  <SwapOutlined />
                </button>
              </Tooltip>
            ) : null}
            <Tooltip title={t('common.delete')} mouseEnterDelay={0.3}>
              <button type="button" className="pcv-media-hover-btn pcv-media-hover-btn--danger" aria-label={t('common.delete')} onClick={handleDeleteClick}>
                <CloseOutlined />
              </button>
            </Tooltip>
          </div>
        </div>
        <div
          className="pcv-media-body"
          style={{ height: hasPreviewSurface ? box.height : undefined }}
          onClick={entityId ? onMediaSingleClick : undefined}
          onDoubleClick={entityId ? onMediaDoubleClick : undefined}
          onDragOver={!entityId && !localUpload ? handleOsFileDragOver : undefined}
          onDrop={!entityId && !localUpload ? handleOsFileDrop : undefined}
        >
          {entityId && imageLoadFailed ? (
            <div className="pcv-media-local-upload-overlay pcv-media-local-upload-overlay--error nodrag" data-testid="image-asset-load-failed">
              <span className="pcv-media-local-upload-label">{t('mediaCard.imageLoadFailed')}</span>
              <div className="pcv-media-local-upload-actions">
                <Button size="small" icon={<ReloadOutlined />} onClick={handleImageLoadRetryClick} data-testid="image-asset-retry-load">
                  {t('common.retry')}
                </Button>
              </div>
            </div>
          ) : null}
          {(entityId ? imageDisplaySrc : localUpload?.previewUrl) && !imageLoadFailed ? (
            <img
              className="pcv-media-image"
              src={entityId ? (imageDisplaySrc as string) : (localUpload?.previewUrl as string)}
              alt=""
              draggable={false}
              loading={entityId ? 'lazy' : undefined}
              data-quality-role={entityId ? imageQualityRole : 'local_upload_preview'}
              onLoad={(e) => {
                setBox(computeMediaBoxSize(e.currentTarget.naturalWidth, e.currentTarget.naturalHeight))
                if (entityId) progressiveImage.markLoaded()
              }}
              onError={entityId ? handleCanonicalImageError : undefined}
            />
          ) : entityId && !imageLoadFailed && !mediaGate.isNearViewport ? (
            /* VistoeraTASK-00147 §四 — genuinely off-screen: no request has
             * even been issued yet (by design), so this is a neutral
             * placeholder, never a loading spinner (nothing is actually
             * happening). */
            <div className="pcv-media-placeholder" data-testid="image-asset-offscreen-placeholder">
              <PictureOutlined />
            </div>
          ) : !entityId && !localUpload?.previewUrl ? (
            <div className="pcv-media-placeholder">
              <PictureOutlined />
              {!hasPreviewSurface ? <span className="pcv-media-placeholder-hint">{t('mediaCard.clickToSelectImage')}</span> : null}
            </div>
          ) : null}
          {/* VistoeraTASK-00147 §六/§七/§十一 — a real MEDIA-loading state,
           * fully independent of any generation status: the request has
           * been issued (node is near viewport) but the browser hasn't
           * decoded it yet. Never "正在生成" — this node type has no
           * generation concept at all. */}
          {entityId && imageMediaLoading && !imageLoadFailed ? (
            <div className="pcv-media-loading-overlay" data-testid="image-asset-media-loading">
              <LoadingOutlined spin />
              <span>{t('mediaCard.imageLoading')}</span>
            </div>
          ) : null}
          {/* Upload-in-progress / failed overlay (spec §4 "上传中状态"/
           * "明确显示失败状态；提供 Retry / Remove") — a local-only node,
           * never reflects any persisted status field. */}
          {localUpload?.status === 'uploading' ? (
            <div className="pcv-media-local-upload-overlay" data-testid="image-asset-uploading">
              <LoadingOutlined spin />
              <span className="pcv-media-local-upload-filename">{localUpload.filename}</span>
              <span className="pcv-media-local-upload-label">{t('mediaCard.uploadingImage')}</span>
            </div>
          ) : null}
          {/* VistoeraTASK-00229 §16 — a brief, automatic, non-terminal
           * state: the upload request itself came back ambiguous (no
           * response / gateway-timeout-style), so before ever showing
           * "上传失败" we ask the asset list whether it actually
           * succeeded anyway. Deliberately no Retry/Remove here — this
           * is not yet a decision the user needs to make, and it
           * resolves to either the success path (overlay disappears
           * entirely) or `'error'` (below) within one round-trip. */}
          {localUpload?.status === 'reconciling' ? (
            <div className="pcv-media-local-upload-overlay" data-testid="image-asset-reconciling">
              <LoadingOutlined spin />
              <span className="pcv-media-local-upload-filename">{localUpload.filename}</span>
              <span className="pcv-media-local-upload-label">{t('mediaCard.confirmingUploadStatus')}</span>
            </div>
          ) : null}
          {localUpload?.status === 'error' ? (
            <div className="pcv-media-local-upload-overlay pcv-media-local-upload-overlay--error nodrag" data-testid="image-asset-upload-failed">
              <span className="pcv-media-local-upload-label">{t('mediaCard.uploadImageFailed')}</span>
              <div className="pcv-media-local-upload-actions">
                <Button size="small" icon={<ReloadOutlined />} onClick={handleRetryLocalUploadClick} data-testid="image-asset-retry-upload">
                  {t('common.retry')}
                </Button>
                <Button size="small" danger icon={<CloseOutlined />} onClick={handleRemoveLocalUploadClick} data-testid="image-asset-remove-upload">
                  {t('common.delete')}
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {/* VistoeraTASK-00067 §2/§3/§4 — expanded inspector strip, same
       * always-visible-below-body pattern `video_media`/`generator` cards
       * use (`.pcv-gen-inspector`, sibling of `.pcv-node-inner`, never
       * inside the header). Deliberately ONE primary action only — no
       * delete/duplicate/save/version-history/crop shortcuts here (those
       * stay exactly where they already are: header hover row for
       * delete/copy/replace, and "截图" lives inside the editor's own
       * toolbar once opened). */}
      {showInspector && entityId ? (
        <div className="pcv-gen-inspector nodrag" data-testid="image-asset-expanded">
          <Button
            block
            icon={<EditOutlined />}
            onClick={handleEditImageClick}
            disabled={!workspaceId || !siteId}
            data-testid="image-asset-edit-button"
          >
            {t('mediaCard.editImage')}
          </Button>
        </div>
      ) : null}

      {/* Right-side Contextual Creation Rail (spec §6–§9) —
       * "生成图片"/"生成视频"/"生成文本", only meaningful once this card
       * actually has an image. A sibling of `.pcv-node-inner` (see
       * comment on the left Handle above) so it renders fully outside
       * the card, never clipped, with a real ~14px hover-bridge gap
       * (see the CSS) so moving the mouse from the card into the rail
       * never drops through a dead zone. Clicking any of the three
       * auto-creates the downstream node WITH this image wired in as
       * its reference/context (never a bare empty node, never an
       * immediate/auto-run generation — spec §10/§12). */}
      {entityId ? <MediaWorkflowRail sourceNodeType="image_asset" onQuickGenerate={handleQuickGenerate} /> : null}

      {/* Enlarged, hover-only, circular connect-out handle (spec §7) —
       * `.pcv-handle--lg` in CSS is what actually enlarges/centers it;
       * visibility itself is gated by `.pcv-node--media:hover` there too. */}
      <Handle type="source" position={Position.Right} id="image-out" className="pcv-handle pcv-handle--lg" />

      <MediaLightbox
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        kind="image"
        src={displayPreviewUrl || thumbnailUrl}
        masterSrc={entityId && workspaceId ? siteAssetMasterUrl(workspaceId, entityId) : null}
        masterMeta={masterMeta}
        assetId={entityId}
      />

      {/* P5/P6/P35 — mounted ONLY while `editorOpen`; closing sets this
       * back to `false`, which unmounts (never `display:none`) both this
       * `Suspense` boundary's child and the heavy `DualPaneCutoutEditor`
       * runtime it holds — no lingering canvas/mask buffers, Object URLs,
       * or event listeners once closed (mediaLoader.ts already revokes
       * its Blob URLs synchronously after decode regardless). */}
      {editorOpen && entityId && workspaceId && siteId ? (
        // Editor Stability P0 (VistoeraTASK-00103) — `key={editorInstanceKey}`
        // on the BOUNDARY (not just the overlay inside it) is deliberate:
        // React remounts a `key`-changed element from scratch, including
        // resetting a class component's own state — this is what makes
        // "重试" actually clear `hasError` back to `false`, not just
        // remount a fresh editor underneath a boundary still stuck showing
        // its old fallback. Root Canvas Error Boundary / node / edges /
        // viewport / selection are all siblings/ancestors of this node
        // entirely outside this subtree — untouched either way (P35/P39).
        <Suspense fallback={null}>
          <ImageEditorErrorBoundary
            key={editorInstanceKey}
            onClose={() => setEditorOpen(false)}
            onRetry={handleEditorRetry}
            diagnosticsRef={diagnosticsRef}
          >
            <CanvasImageEditorOverlay
              workspaceId={workspaceId}
              siteId={siteId}
              sourceAssetId={entityId}
              sourceLabel={label}
              sourceCanvasId={nodeData.projectId}
              sourceNodeId={id}
              // VistoeraTASK-00145 — already-fetched original dimensions
              // (see `masterMeta`'s own load effect above) enable the fast
              // placeholder-then-full-resolution editor load path. `null`
              // if `masterMeta` hasn't resolved yet by the time the user
              // clicks "编辑" — the overlay simply falls back to today's
              // single-fetch behavior for that one open, no regression.
              knownSourceWidth={masterMeta?.width ?? null}
              knownSourceHeight={masterMeta?.height ?? null}
              knownSourceSizeBytes={masterMeta?.sizeBytes ?? null}
              onClose={() => setEditorOpen(false)}
              onSavedAsNewAsset={handleEditorSavedAsNewAsset}
              diagnosticsRef={diagnosticsRef}
            />
          </ImageEditorErrorBoundary>
        </Suspense>
      ) : null}
    </div>
  )
}

export default memo(ImageAssetNodeImpl)
